import { Alert } from "@/src/lib/productAlert";
import { Text } from "@/src/components/ui/BrandText";
import { border, surface, text as uiText, brand } from "@/src/theme/tokens";
import { Ionicons } from "@/src/components/ui/ionicons";
import * as WebBrowser from "expo-web-browser";
import { router } from "expo-router";
import React from "react";
import { Pressable, View } from "react-native";
import { PostMediaGallery } from "./PostMediaGallery";
import { PostAuthorHeader } from "./PostAuthorHeader";
import { PostText } from "./PostText";
import type { FirePost } from "../types/models";
import { getPostImageUrls } from "../lib/postImages";
import { isShortTextOnlyPost } from "../lib/postPresentation";

const CARD = surface.primary;

function getPostImages(post: FirePost) {
  return getPostImageUrls(post);
}

type Props = {
  item: FirePost & { subjectLabel?: string };
  liked: boolean;
  likes: number;
  isMine: boolean;
  onToggleLike: (postId: string) => void | Promise<void>;
  onOpenActions: (item: FirePost) => void;
  onSharePost?: (item: FirePost) => void;
  disableGroupLink?: boolean;
  disableAuthorLink?: boolean;
};

function PostCardBase({
  item,
  liked,
  likes,
  isMine,
  onToggleLike,
  onOpenActions,
  onSharePost,
  disableGroupLink = false,
  disableAuthorLink = false,
}: Props) {
  const imageUrls = React.useMemo(() => getPostImages(item), [item]);
  const shortTextOnly = isShortTextOnlyPost(item);

  const handleLikePress = React.useCallback(async () => {
    try {
      await onToggleLike(item.id);
    } catch (e: any) {
      if (__DEV__) console.warn("[post] like failed", e?.code ?? "unknown");
      Alert.alert("Action impossible", "Le like n'a pas pu être enregistré. Réessaie.");
    }
  }, [item.id, onToggleLike]);

  const handleOpenActions = React.useCallback(() => {
    onOpenActions(item);
  }, [item, onOpenActions]);

  const handleOpenComments = React.useCallback(() => {
    router.push(`/post/${item.id}`);
  }, [item.id]);

  const handleOpenPost = React.useCallback(() => {
    router.push(`/post/${item.id}`);
  }, [item.id]);

  const handleOpenGroup = React.useCallback(() => {
    if (!disableGroupLink && item.groupId) {
      router.push(`/group/${item.groupId}`);
    }
  }, [disableGroupLink, item.groupId]);

  const handleOpenAttachment = React.useCallback(async () => {
    if (!item.attachmentUrl) return;
    try {
      await WebBrowser.openBrowserAsync(item.attachmentUrl);
    } catch (e: any) {
      if (__DEV__) console.warn("[post] attachment open failed", e?.code ?? "unknown");
      Alert.alert("Ouverture impossible", "La pièce jointe n'a pas pu être ouverte.");
    }
  }, [item.attachmentUrl]);

  const handleSharePost = React.useCallback(() => {
    onSharePost?.(item);
  }, [item, onSharePost]);

  return (
    <>
      <View
        style={{
          borderRadius: 18,
          backgroundColor: CARD,
          borderWidth: 1,
          borderColor: border.subtle,
          overflow: "hidden",
        }}
      >
        <View style={{ padding: 16 }}>
          <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <PostAuthorHeader
                post={item}
                disableAuthorLink={disableAuthorLink}
                disableGroupLink={disableGroupLink}
                onAuthorPress={(identityId) => router.push(`/profile/${identityId}`)}
                onGroupPress={() => handleOpenGroup()}
              />
            </View>

            <Pressable
              onPress={handleOpenActions}
              accessibilityRole="button"
              accessibilityLabel="Ouvrir les actions du post"
              hitSlop={8}
              style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
            >
              <Ionicons name="ellipsis-vertical" size={18} color={uiText.secondary} />
            </Pressable>
          </View>

          {isMine && (item.moderationStatus === "pending" || item.moderationStatus === "review") ? (
            <View style={{ alignSelf: "flex-start", marginTop: 10, borderRadius: 999, paddingHorizontal: 9, paddingVertical: 5, backgroundColor: surface.warning, borderWidth: 1, borderColor: brand.accent }}>
              <Text style={{ color: brand.accent, fontWeight: "900", fontSize: 12 }}>En révision</Text>
            </View>
          ) : null}

          {!!item.text || imageUrls.length > 0 || (item.attachmentType === "pdf" && item.attachmentUrl) ? (
            <View style={{ marginTop: 16 }}>
              {!!item.text ? (
                <PostText text={item.text} shortTextOnly={shortTextOnly} onOpenPost={handleOpenPost} />
              ) : null}

              <PostMediaGallery imageUrls={imageUrls} topSpacing={item.text ? 10 : 0} />

              {item.attachmentType === "pdf" && item.attachmentUrl ? (
                <Pressable
                  onPress={handleOpenAttachment}
                  accessibilityRole="button"
                  accessibilityLabel={`Ouvrir la pièce jointe ${item.attachmentName || "PDF"}`}
                  style={{
                    marginTop: item.text || imageUrls.length ? 10 : 0,
                    minHeight: 48,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: border.subtle,
                    backgroundColor: surface.overlay,
                    paddingHorizontal: 12,
                    paddingVertical: 9,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <Ionicons name="document-text-outline" size={20} color={uiText.primary} />
                  <Text style={{ color: uiText.primary, fontWeight: "800", flex: 1 }} numberOfLines={1}>
                    {item.attachmentName || "Document PDF"}
                  </Text>
                  <Text style={{ color: uiText.brand, fontWeight: "800", fontSize: 12 }}>Ouvrir</Text>
                  <Ionicons name="open-outline" size={16} color={uiText.brand} />
                </Pressable>
              ) : null}
            </View>
          ) : null}
        </View>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            paddingHorizontal: 16,
            paddingBottom: 14,
          }}
        >
          <Pressable
            onPress={handleLikePress}
            accessibilityRole="button"
            accessibilityLabel={liked ? `Retirer le j'aime, ${likes} j'aime` : `Aimer le post, ${likes} j'aime`}
            style={{ minHeight: 44, flexDirection: "row", alignItems: "center", gap: 7, paddingHorizontal: 2 }}
          >
            <Ionicons
              name={liked ? "heart" : "heart-outline"}
              size={18}
              color={liked ? uiText.danger : uiText.secondary}
            />
            <Text style={{ color: uiText.muted, fontWeight: "700" }}>{likes}</Text>
          </Pressable>

          <Pressable
            onPress={handleOpenComments}
            accessibilityRole="button"
            accessibilityLabel="Ouvrir les commentaires"
            accessibilityHint="Voir la publication et ses commentaires"
            style={{ minHeight: 44, minWidth: 44, flexDirection: "row", alignItems: "center", gap: 7, paddingHorizontal: 2 }}
          >
            <Ionicons name="chatbubble-outline" size={18} color={uiText.primary} />
            <Text style={{ color: uiText.primary, fontWeight: "800" }}>Commenter</Text>
          </Pressable>

          <Pressable
            onPress={handleSharePost}
            accessibilityRole="button"
            accessibilityLabel="Partager le post"
            accessibilityHint="Partager cette publication"
            style={{ minHeight: 44, minWidth: 44, flexDirection: "row", alignItems: "center", gap: 7, paddingHorizontal: 2 }}
          >
            <Ionicons name="paper-plane-outline" size={18} color={uiText.primary} />
            <Text style={{ color: uiText.primary, fontWeight: "800" }}>Partager</Text>
          </Pressable>
        </View>
      </View>
    </>
  );
}

export const PostCard = React.memo(PostCardBase);
